import React, { useMemo, useState } from "react";
import Input from "../../ui/Input";
import { driverInactivityAlert, driverStateDisplay } from "../model/driverActivity";
import { followUpPill } from "../model/driverFocus";
import { searchKey } from "../model/towerFormat";
import type { TowerDashboard, TowerDriverDetail, TowerShipment } from "../../../types/controlTower";

export type FinderSection = "depot" | "unclosed";
type Suggestion =
  | { key: string; driver: TowerDriverDetail; reason: string | null }
  | { key: string; section: FinderSection; reason: string };

// Resultados del buscador: choferes por nombre o usuario de Lightdata (no se
// muestra, pero se puede buscar) y, si no, por un envío suyo (tracking,
// cliente o localidad); después, envíos del depósito y de "Sin cerrar".
function findSuggestions(data: TowerDashboard, query: string): Suggestion[] {
  if (query.length < 2) return [];
  const matchesText = (s: TowerShipment) => searchKey(`${s.tracking} ${s.client} ${s.locality}`).includes(query);
  const found: Suggestion[] = [];
  for (const driver of data.drivers) {
    if (driver.kind === "internal") continue;
    if (searchKey(`${driver.name} ${driver.usernames.join(" ")}`).includes(query)) {
      found.push({ key: driver.id, driver, reason: null });
      continue;
    }
    const shipment = driver.shipments.find(matchesText);
    if (shipment) found.push({ key: driver.id, driver, reason: `${shipment.client} · ${shipment.tracking}` });
  }
  for (const [section, list] of [
    ["depot", data.depot?.shipments || []],
    ["unclosed", data.unclosed || []],
  ] as const) {
    const shipment = list.find(matchesText);
    if (shipment) found.push({ key: section, section, reason: `${shipment.client} · ${shipment.tracking}` });
  }
  const rank = (entry: Suggestion) => ("driver" in entry ? (entry.reason === null ? 0 : 1) : 2);
  const name = (entry: Suggestion) => ("driver" in entry ? entry.driver.name : entry.section);
  return found.sort((a, b) => rank(a) - rank(b) || name(a).localeCompare(name(b), "es")).slice(0, 8);
}

// Buscador de Monitoreo: muestra los resultados en una lista debajo del campo
// (no despliega las zonas). Flechas para moverse, Enter abre, Esc cierra.
export default function TowerDriverFinder({
  data,
  search,
  onSearchChange,
  alertsEnabled,
  nowMs,
  onSelectDriver,
  onOpenSection,
}: {
  data: TowerDashboard;
  search: string;
  onSearchChange: (value: string) => void;
  alertsEnabled: boolean;
  nowMs: number;
  onSelectDriver: (driverId: string) => void;
  onOpenSection: (section: FinderSection) => void;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const query = searchKey(search.trim());
  const suggestions = useMemo(() => findSuggestions(data, query), [data, query]);
  const pick = (entry: Suggestion) => {
    setOpen(false);
    setActive(0);
    if ("driver" in entry) onSelectDriver(entry.driver.id);
    else onOpenSection(entry.section);
  };

  return (
    <div
      className="tower-search tower-search-finder"
      onBlur={(e) => {
        // Se cierra solo cuando el foco sale del buscador (no al pasar a un resultado).
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <Input
        label="Buscar"
        type="search"
        placeholder="Chofer, localidad, tracking o cliente…"
        value={search}
        aria-expanded={open && suggestions.length > 0}
        aria-controls="tower-driver-finder"
        onChange={(e) => {
          onSearchChange(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
            const step = e.key === "ArrowDown" ? 1 : -1;
            setActive((i) => Math.min(Math.max(i + step, 0), Math.max(suggestions.length - 1, 0)));
          }
          if (e.key === "Enter" && suggestions.length) {
            e.preventDefault();
            pick(suggestions[Math.min(active, suggestions.length - 1)]);
          }
        }}
      />
      {open && query.length >= 2 && (
        <ul id="tower-driver-finder" className="tower-finder" role="listbox" aria-label="Choferes encontrados">
          {suggestions.length === 0 && <li className="tower-finder-empty">No hay choferes que coincidan.</li>}
          {suggestions.map((entry, index) => {
            let title: string;
            let detail: string;
            let pill: { label: string; tone: string };
            if ("driver" in entry) {
              const { driver } = entry;
              const alert = alertsEnabled ? driverInactivityAlert(driver, driver, nowMs) : null;
              pill = followUpPill(alert, driverStateDisplay(driver.state));
              const zones = driver.zones.filter((z) => z !== "Sin clasificar").join(" / ") || "Sin zona";
              title = driver.name;
              detail = `Zona ${zones} · ${driver.counts.flexInTransit} Flex en camino${entry.reason ? ` · ${entry.reason}` : ""}`;
            } else {
              title = entry.section === "depot" ? "Envíos en depósito" : "Sin cerrar en Lightdata";
              detail = entry.reason;
              pill = { label: entry.section === "depot" ? "Depósito" : "Sin cerrar", tone: "other" };
            }
            return (
              <li key={entry.key} role="option" aria-selected={index === active}>
                <button
                  type="button"
                  className={index === active ? "active" : undefined}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => pick(entry)}
                >
                  <span className="tower-finder-main">
                    <strong>{title}</strong>
                    <small>{detail}</small>
                  </span>
                  <span className={`tower-state ${pill.tone}`}>{pill.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
